import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const companyServices = [
    { name: 'Private Limited Company', path: '/services/private-limited' },
    { name: 'LLP Registration', path: '/services/llp' },
    { name: 'One Person Company', path: '/services/opc' },
    { name: 'Proprietorship', path: '/services/proprietorship' },
    { name: 'Section 8 Company', path: '/services/section-8' },
    { name: 'Nidhi Company', path: '/services/nidhi-company' },
  ];

  const certificates = [
    { name: 'GST Registration', path: '/services/gst' },
    { name: 'FSSAI License', path: '/services/fssai' },
    { name: 'Trademark Registration', path: '/services/trademark' },
    { name: 'IEC Code', path: '/services/iec' },
    { name: 'ESI Registration', path: '/services/esi' },
    { name: 'PF Registration', path: '/services/pf' },
    { name: 'Professional Tax', path: '/services/pt' },
    { name: 'MSME Registration', path: '/services/msme' },
    { name: 'Shop & Establishment', path: '/services/shop-establishment' },
    { name: 'Startup India', path: '/services/startup-india' },
  ];

  const compliance = [
    { name: 'Pvt Ltd Compliance', path: '/services/pvt-ltd-compliance' },
    { name: 'LLP Compliance', path: '/services/llp-compliance' },
    { name: 'OPC Compliance', path: '/services/opc-compliance' },
    { name: 'Proprietorship Compliance', path: '/services/proprietorship-compliance' },
    { name: 'Section 8 Compliance', path: '/services/section-8-compliance' },
  ];

  const miscellaneous = [
    { name: 'Director Change', path: '/services/director-change' },
    { name: 'Address Change', path: '/services/address-change' },
    { name: 'MOA Amendment', path: '/services/moa-amendment' },
    { name: 'Increase Authorized Capital', path: '/services/increase-capital' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-10 text-sm">
            {/* Contact Info */}
            <div className="hidden md:flex items-center space-x-6">
              <a href="tel:+919509363261" className="flex items-center space-x-2 text-slate-600 hover:text-blue-600 transition-colors">
                <Phone size={14} />
                <span>+91 9509363261</span>
              </a>
              <a href="mailto:info@nextgenfilling.com" className="flex items-center space-x-2 text-slate-600 hover:text-blue-600 transition-colors">
                <Mail size={14} />
                <span>info@nextgenfilling.com</span>
              </a>
            </div>

            {/* Social Icons */}
            <div className="hidden md:flex items-center space-x-4">
              <a href="#" className="text-slate-500 hover:text-blue-600 transition-colors" aria-label="Facebook">
                <Facebook size={16} />
              </a>
              <a href="#" className="text-slate-500 hover:text-blue-600 transition-colors" aria-label="Twitter">
                <Twitter size={16} />
              </a>
              <a href="#" className="text-slate-500 hover:text-blue-600 transition-colors" aria-label="LinkedIn">
                <Linkedin size={16} />
              </a>
              <a href="#" className="text-slate-500 hover:text-blue-600 transition-colors" aria-label="Instagram">
                <Instagram size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`sticky top-0 z-[100] transition-all duration-300 bg-white border-b border-slate-200 ${
          isScrolled ? 'shadow-md' : ''
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 relative">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="navbar-logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_filing-portal-beta/artifacts/s4nrpvgt_Group%2046%20%283%29.png"
                alt="NextGen Filling Association Logo"
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                  location.pathname === '/' ? 'text-blue-600' : 'text-slate-700 hover:text-blue-600'
                }`}
                data-testid="nav-home-link"
              >
                Home
              </Link>

              <div className="relative group/dropdown">
                <button
                  className="flex items-center text-sm font-medium transition-colors text-slate-700 hover:text-blue-600 uppercase tracking-wide py-2"
                  data-testid="nav-services-dropdown"
                >
                  All Services
                  <ChevronDown className="ml-1 h-4 w-4 transition-transform duration-200 group-hover/dropdown:rotate-180" />
                </button>
                
                {/* Mega Menu Dropdown */}
                <div 
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-0 w-[850px] bg-white rounded-xl shadow-2xl opacity-0 invisible group-hover/dropdown:opacity-100 group-hover/dropdown:visible transition-all duration-300 ease-out transform group-hover/dropdown:translate-y-0 -translate-y-2 z-[9999] border border-slate-100"
                  style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}
                >
                  <div className="p-8">
                    <div className="grid grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 mb-4 uppercase tracking-wider flex items-center">
                        <span className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </span>
                        Company Registration
                      </h3>
                      <div className="space-y-1">
                        {companyServices.map((service) => (
                          <Link
                            key={service.path}
                            to={service.path}
                            className="block px-3 py-2 text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-150"
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 mb-4 mt-6 uppercase tracking-wider flex items-center">
                        <span className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center mr-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                        Compliance
                      </h3>
                      <div className="space-y-1">
                        {compliance.map((service) => (
                          <Link
                            key={service.path}
                            to={service.path}
                            className="block px-3 py-2 text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-150"
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 mb-4 uppercase tracking-wider flex items-center">
                        <span className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center mr-2">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </span>
                        Certificates & Licenses
                      </h3>
                      <div className="space-y-1">
                        {certificates.map((service) => (
                          <Link
                            key={service.path}
                            to={service.path}
                            className="block px-3 py-2 text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-150"
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 mb-4 mt-6 uppercase tracking-wider flex items-center">
                        <span className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center mr-2">
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                          </svg>
                        </span>
                        Miscellaneous
                      </h3>
                      <div className="space-y-1">
                        {miscellaneous.map((service) => (
                          <Link
                            key={service.path}
                            to={service.path}
                            className="block px-3 py-2 text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-150"
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                    {/* Bottom CTA in Dropdown */}
                    <div className="border-t border-slate-100 mt-6 pt-6">
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Need help choosing?</p>
                          <p className="text-xs text-slate-600 mt-1">Talk to our experts</p>
                        </div>
                        <Link to="/contact">
                          <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2">
                            Contact Us
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/about"
                className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                  location.pathname === '/about' ? 'text-blue-600' : 'text-slate-700 hover:text-blue-600'
                }`}
                data-testid="nav-about-link"
              >
                About Us
              </Link>

              <Link to="/contact">
                <Button
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-6 py-2 font-medium shadow-md transition-all"
                  data-testid="nav-contact-button"
                >
                  Get in Touch
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden text-slate-900"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" className="block text-sm font-medium text-slate-900 uppercase" onClick={() => setIsMobileMenuOpen(false)}>
                Home
              </Link>
              <Link to="/about" className="block text-sm font-medium text-slate-900 uppercase" onClick={() => setIsMobileMenuOpen(false)}>
                About Us
              </Link>
              <Link to="/contact" className="block text-sm font-medium text-slate-900 uppercase" onClick={() => setIsMobileMenuOpen(false)}>
                Contact
              </Link>
              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Services</p>
                {[...companyServices, ...certificates, ...compliance, ...miscellaneous].map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    className="block text-sm text-slate-600 py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;